import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { reportEvent } from '~/domains/analytics';
import { Routes } from '~/domains/common/constants/routes';
import { createQueryString } from '~/domains/common/utils/queryString';

export const useShareChannel = () => {
  const router = useRouter();
  const handle = router.query.handle as string;
  const { user } = useContext(UserContext);
  const { t } = useTranslation('channel');

  const handleShareChannel = useCallback(async () => {
    const channelUrl = `${window.location.origin}${Routes.channel(handle || user?.handle)}${createQueryString({
      utm_source: 'SplashWeb',
      utm_medium: 'Channel',
      utm_content: 'btn_copy-link',
      ...(user?.id && { referral: user.id, utm_campaign: user.id }),
    })}`;

    try {
      if (
        /(iPad)|(iPhone)|(iPod)|(android)|(webOS)/iu.exec(navigator.userAgent) &&
        navigator.share
      ) {
        await navigator.share({ title: `@${handle}`, url: channelUrl });
      } else {
        await window.navigator.clipboard.writeText(channelUrl);

        showNotification({
          message: t('notifications.copySuccess'),
          color: 'green',
          autoClose: 2500,
        });
      }

      reportEvent('Channels > Copy Channel Link', {
        handle,
        is_own_channel: user?.handle === handle,
      });
    } catch (err) {
      // Native share throws "AbortError" type when users dismisses the share modal
      // We don't want to show an error for that
      if (err.name !== 'AbortError') {
        showNotification({
          message: t('notifications.copyError', { url: channelUrl }),
          color: 'red',
          autoClose: 2500,
        });
      }
    }
  }, [handle, user?.id, user?.handle, t]);

  return { handleShareMyChannel: handleShareChannel };
};
